import { Module, Mutation, Action } from "vuex-class-modules"
import { store } from "../store"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { Modules } from "../modules"
import { TipsStepType, TipsKeyType, User, UserSettings } from "@/models"
import { UpdateObjectValue } from "@/models/common"
import { FilterUser } from "@/models/Filter/FilterUser"
import { ServerResponse } from "@/models/ServerResponse"
import { BaseModule } from "@/models/BaseModule"
import { AccountActions, accountModule } from "./account.module"
import { sessionModule } from "./session.module"
import { AlertActions, alertModule } from "./alert.module"
import { AlertType, AlertContentItem } from "@/models/Alert"
import { Account } from "@/models/Account"
import { parsingServerResponseContent } from "@/utils/helpers"
import { useRoute } from "vue-router"

export enum UserActions {
  GET_USERS = "GET_USERS",
  RESET_USERS = "RESET_USERS",
  GET_USER_CURRENT = "GET_USER_CURRENT",
  GET_USER_CURRENT_PROFILE = "GET_USER_CURRENT_PROFILE",
  GET_USER_BY_ID = "GET_USER_BY_ID",
  UPDATE_CURRENT_USER = "UPDATE_CURRENT_USER",
  UPDATE_CURRENT_USER_ROLE = "UPDATE_CURRENT_USER_ROLE",
  UPDATE_CURRENT_USER_TIME_ZONE = "UPDATE_CURRENT_USER_TIME_ZONE",
  UPDATE_CURRENT_USER_VALUE = "UPDATE_CURRENT_USER_VALUE",
  POST_SELF_PICTURE = "POST_SELF_PICTURE",
  DELETE_SELF_PICTURE = "DELETE_SELF_PICTURE",
  UPDATE_PROFILE_SETTINGS = "UPDATE_PROFILE_SETTINGS",
  GET_USER_CURRENT_PROFILE_SETTINGS = "GET_USER_CURRENT_PROFILE_SETTINGS",
  USER_REMOVE_FROM_ACCOUNT_SUPER_ADMIN = "USER_REMOVE_FROM_ACCOUNT_SUPER_ADMIN",
  USER_REMOVE_FROM_ACCOUNT_ADMIN = "USER_REMOVE_FROM_ACCOUNT_ADMIN",
  UPDATE_ACCOUNT_URL_NAME_IN_CURRENT_PROFILE = "UPDATE_ACCOUNT_URL_NAME_IN_CURRENT_PROFILE",
  REMOVE_USER_FROM_LIST = "REMOVE_USER_FROM_LIST",
  THEME_IS_DARK = "THEME_IS_DARK",
  TIPS_KEY = "TIPS_KEY",
  GET_TIPS_STEPS = "GET_TIPS_STEPS",
  GET_SELECTED_TIPS_STEP = "GET_SELECTED_TIPS_STEP",
  SET_SELECTED_STEP = "SET_SELECTED_STEP",
  RESET_TIPS_DATA = "RESET_TIPS_DATA",
  USER_SEARCH_TEXT = "USER_SEARCH_TEXT"
}

interface UpdateCurrentUserTimeZonePayload {
  timeZone?: string
  overwrite?: boolean
}

type UpdateCurrentUserTimeZoneResponse = string | { timeZone?: string }

@Module
class UserModule extends BaseModule {
  users: User[] = []
  user: User
  userProfile: User
  tipsSteps: TipsStepType[] = []
  tipsStepKey: TipsKeyType = ""
  selectedStep: TipsStepType | null = null
  searchUserText = ""

  get accountId() {
    return accountModule.accountId
  } 
  
  get [`get/${UserActions.GET_TIPS_STEPS}`](): any {
    return this.tipsSteps
  }

  get [`get/${UserActions.TIPS_KEY}`](): any {
    return this.tipsStepKey
  }

  get [`get/${UserActions.GET_SELECTED_TIPS_STEP}`](): any {
    return this.selectedStep
  }

  get [`get/${UserActions.GET_USER_CURRENT}`]() {
    return this.user
  }

  get [`get/${UserActions.GET_USERS}`]() {
    return this.users
  }

  get [`get/${UserActions.GET_USER_CURRENT_PROFILE}`]() {
    return this.userProfile
  }

  get [`get/${UserActions.GET_USER_CURRENT_PROFILE_SETTINGS}`]() {
    return this.userProfile?.settings
  }

  get [`get/${UserActions.USER_SEARCH_TEXT}`]() {
    return this.searchUserText
  }

  get [`get/${UserActions.THEME_IS_DARK}`]() {
    const localStorageTheme = localStorage.getItem('darkTheme') === 'true'
    const profileTheme = this.userProfile?.settings?.darkTheme
    const router = useRoute()
    const isDark = router?.meta?.disabledDarkMode ? false : profileTheme ?? localStorageTheme
    if (!isDark) {
      document.body.classList.remove("dark")
      document.querySelector(":root")?.classList.remove("dark")
    } else {
      document.body.classList.add("dark")
      document.querySelector(":root")?.classList.add("dark")
    }
    return isDark
  }

  @Mutation
  getSteps({ learnStep, customTipsSteps, customDisableTips } : {learnStep: TipsKeyType, customTipsSteps?: TipsStepType[], customDisableTips?: TipsStepType[] }) {
    const userSettings = this.userProfile?.settings
    if(!userSettings) {
      this.tipsSteps = []
      this.selectedStep = null
      return
    }
    const canShowStep = userSettings[learnStep]
    if(!canShowStep) {
      this.tipsSteps = []
      this.selectedStep = null
      return
    }
    this.tipsStepKey = learnStep
    switch (learnStep) {
      case "showFirstEntranceOnboardingTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu", "switchTeamBacklog"]
        break
      }
      case "showOnboardingTips": {
        this.tipsSteps = ["backlogAnalytics", "backlogAnalyticsButton", "switchTeamBacklog", "breadcrumbs", "headerMenu", "footerMenu"]
        break
      }
      case "showFirstItemCreationTips": {
        this.tipsSteps = ["itemLeft", "itemRight"]
        break
      }
      case "showRoadMapSetupTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu"]
        break
      }
      case "showUnprioritizedTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu", "switchTeamBacklog", "backlogUnprioritized"]
        break
      }
      case "showMilestoneSetupTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu", "createNewMilestone"]
        break
      }
      case "showEpicSetupTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu", "createNewEpic"]
        break
      }
      case "showEpicInlineItemCreationTips": {
        this.tipsSteps = ["breadcrumbs", "headerMenu", "footerMenu", "createInlineItem"]
        break
      }
      // case "showProjectTeamTypesTips": {
      //   this.tipsSteps = ["teamTypes"]
      //   break
      // }
      default: {
        this.tipsSteps = []
      }
    }
    if(customTipsSteps && customTipsSteps.length) {
      this.tipsSteps = [...this.tipsSteps, ...customTipsSteps]
    }
    if(customDisableTips && customDisableTips.length) {
      this.tipsSteps = this.tipsSteps.filter(e => !customDisableTips.includes(e))
    }
    const shownTips = userSettings.shownTips ?? []
    this.tipsSteps = this.tipsSteps.filter(e => !shownTips.includes(e))
    this.selectedStep = this.tipsSteps[0] ?? null
  }

  @Mutation
  setRefPosition(text: string) {
    this.searchUserText = text
  }

  @Mutation
  setSelectedStep(step: TipsStepType | null) {
    this.selectedStep = step
  }

  @Mutation
  resetTipsData() {
    this.tipsSteps = []
    this.tipsStepKey = ""
    this.selectedStep = null
  }

  @Mutation
  setUserProfile(userProfile: User) {
    this.userProfile = userProfile
  }

  @Mutation
  setUserValue(payload: UpdateObjectValue<User>) {
    Object.assign(this.userProfile, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  updateUserSettings(settings: UserSettings) {
    this.userProfile.settings = { ...this.userProfile.settings, ...settings }
  }

  @Mutation
  setCurrentUserTimeZone(timeZone: string) {
    if (this.userProfile) {
      this.userProfile.timeZone = timeZone
      this.userProfile.settings = {
        ...this.userProfile.settings,
        timeZone
      }
    }
    if (this.user) {
      this.user.timeZone = timeZone
      this.user.settings = {
        ...this.user.settings,
        timeZone
      }
    }
  }

  @Mutation
  setUsers(serverResponse: ServerResponse<User>) {
    this.users = parsingServerResponseContent({
      originalContent: this.users,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber,
    })
  }

  @Mutation
  resetUsers() {
    this.users = []
  }

  @Mutation
  removeUserFromList(userId: string) {
    this.users = this.users.filter(e => e.id !== userId)
  }

  @Mutation
  setUser(user: User) {
    this.user = user
  }

  @Mutation
  setNewAccountUrlName(account: Account) {
    if (account.id !== this.userProfile.accountId) return

    const updateAccounts = this.userProfile.accounts.map((e) => {
      if (e.id === account.id && account.urlName !== e.urlName) {
        e.urlName = account.urlName
      }
      return e
    })
    userModule[UserActions.UPDATE_CURRENT_USER_VALUE]({
      id: "accounts",
      value: updateAccounts,
    } as any)
  }


  @Mutation
  setUserPictureUrl({ pictureUrl }: { pictureUrl: string }) {
    this.userProfile.picture = this.userProfile.picture || {}
    this.userProfile.picture.pictureUrl = pictureUrl
    this.userProfile.pictureUrl = pictureUrl
  }

  @Mutation
  setJiraUserToken(jiraUserToken: string) {
    this.userProfile.jiraApiToken = jiraUserToken
  }

  @Mutation
  deleteJiraUserToken() {
    delete this.userProfile.jiraApiToken
  }

  @Action
  async [UserActions.GET_USERS](params: FilterUser) {
    const filter = FilterUser.create(params)
    const endpoint = filter.extended ? Endpoint.GET_USERS_EXTENDED(this.accountId) : Endpoint.GET_USERS(this.accountId)

    const data = await apiService.get<ServerResponse<User>>(endpoint, filter.getJsonObj())
    return this.handleResponse<ServerResponse<User>>(data, (data) => {
      const content: User[] = data.content.map(c => User.create(c))
      data = { ...data, content }
      if (filter.forSearch) {
        return data
      }
      this.setUsers(data)
      return data
    })
  }

  @Action
  async [UserActions.GET_USER_CURRENT]() {
    const data = await apiService.get<User>(Endpoint.USER_CURRENT())
    return this.handleResponse<User>(data, (data) => {
      this.setUser(User.create(data))
    })
  }
  

  @Action
  async [UserActions.GET_USER_BY_ID](id: string) {
    const data = await apiService.get<User>(Endpoint.GET_USER_BY_ID(id))
    return this.handleResponse<User>(data, (data) => {
      this.setUser(User.create(data))
    })
  }

  @Action
  async [UserActions.GET_USER_CURRENT_PROFILE]() {
    const data = await apiService.get<User>(Endpoint.USER_CURRENT_PROFILE())
    return this.handleResponse<User>(data, async (data) => {
      const userProfile = User.create(data)
      this.setUserProfile(userProfile)
      accountModule.setAccountOverview(userProfile)
      sessionModule.setAccountOverview(userProfile)
      await this[UserActions.UPDATE_CURRENT_USER_TIME_ZONE]()
      return userProfile
    })
  }

  @Action
  async [UserActions.UPDATE_CURRENT_USER](user: User) {
    const data = await apiService.patch<User>(Endpoint.USER_CURRENT(), user)
    return this.handleResponse<User>(data, (data) => new User(data))
  }

  @Action
  async [UserActions.UPDATE_CURRENT_USER_ROLE](role: string) {
    const data = await apiService.patch<User>(Endpoint.USER_CURRENT_ROLE(role))
    return this.handleResponse<User>(data)
  }

  @Action
  async [UserActions.UPDATE_CURRENT_USER_TIME_ZONE](
    payload: UpdateCurrentUserTimeZonePayload = {}
  ) {
    if (this.userProfile?.timeZone && !payload.overwrite) return true

    const detectedTimeZone =
      payload.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!detectedTimeZone) return false

    const data = await apiService.patch<UpdateCurrentUserTimeZoneResponse>(
      Endpoint.USER_CURRENT_TIME_ZONE(),
      JSON.stringify(detectedTimeZone),
      {
        headers: {
          "Content-Type": "application/json"
        }
      }
    )
    return this.handleResponse<UpdateCurrentUserTimeZoneResponse>(data, data => {
      const savedTimeZone =
        typeof data === "string" ? data : data.timeZone ?? detectedTimeZone

      this.setCurrentUserTimeZone(savedTimeZone)
      return true
    }, true)
  }

  @Action
  async [UserActions.POST_SELF_PICTURE](picture: Blob) {
    const file = new FormData()
    file.append("file", picture)
    const data = await apiService.post<string>(Endpoint.USER_CURRENT_PICTURE(), file)
    return this.handleResponse<string>(data, (data) => {
      const alertContent: AlertContentItem[] = [
        {
          text: "User Picture",
          type: "bold",
        },
        {
          text: " updated",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      this.setUserPictureUrl({
        pictureUrl: data,
      })
    }, true,  (error)=> {
      const alertContent: AlertContentItem[] = [
        {
          text: error.response.data?.message || "Error while uploading picture",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.ERROR,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [UserActions.DELETE_SELF_PICTURE]() {
    const data = await apiService.delete<User>(Endpoint.USER_CURRENT_PICTURE())
    return this.handleResponse<User>(data)
  }

  @Action
  async [UserActions.UPDATE_PROFILE_SETTINGS]({ settings, immediate = false, showToast = false }: { settings: UserSettings, immediate?: boolean, showToast?: boolean }) {
    if (immediate) this.updateUserSettings(settings)
    const data = await apiService.put<UserSettings>(Endpoint.UPDATE_PROFILE_SETTINGS(), settings)
    return this.handleResponse<UserSettings>(data, (data) => {
      if (!immediate) this.updateUserSettings(data)
      if (showToast) {
        const alertContent: AlertContentItem[] = [
          { 
            text: "User Settings", 
            type: "bold" 
          },
          { 
            text: " updated", 
            type: "regular" 
          }
        ]
        alertModule[AlertActions.SHOW_ALERT]({
          type: AlertType.SUCCESS,
          theme: "toast",
          content: alertContent
        })
        
      }
    })
  }

  @Action
  [UserActions.UPDATE_CURRENT_USER_VALUE](payload: UpdateObjectValue<User>) {
    this.setUserValue(payload)
  }

  @Action
  [UserActions.UPDATE_ACCOUNT_URL_NAME_IN_CURRENT_PROFILE](account: Account) {
    this.setNewAccountUrlName(account)
  }

  @Action
  [UserActions.RESET_USERS]() {
    this.resetUsers()
  }

  @Action
  [UserActions.REMOVE_USER_FROM_LIST](userId: string) {
    this.removeUserFromList(userId)
  }

  @Action
  [UserActions.GET_TIPS_STEPS]({ learnStep, customTipsSteps, customDisableTips } : {learnStep: TipsKeyType, customTipsSteps?: TipsStepType[], customDisableTips?: TipsStepType[] }) {
    this.getSteps({ learnStep, customTipsSteps, customDisableTips })
  }

  @Action
  [UserActions.SET_SELECTED_STEP](step: TipsStepType | null) {
    this.setSelectedStep(step)
  }

  @Action
  [UserActions.RESET_TIPS_DATA]() {
    this.resetTipsData()
  }

  @Action
  [UserActions.USER_SEARCH_TEXT](text: string) {
    this.setRefPosition(text)
  }


  @Action
  async [UserActions.USER_REMOVE_FROM_ACCOUNT_SUPER_ADMIN]({ userId, accountId }) {
    const data = await apiService.delete<void>(Endpoint.SUPERADMIN_USER_BY_ID(accountId, userId))
    return this.handleResponse<void>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: "User",
          type: "bold",
        },
        {
          text: " deleted",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [UserActions.USER_REMOVE_FROM_ACCOUNT_ADMIN]({ userId, accountId }) {
    const data = await apiService.delete<void>(Endpoint.ADMIN_USER_BY_ID(accountId, userId))
    return this.handleResponse<void>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: "User",
          type: "bold",
        },
        {
          text: " deleted",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }
}

export const userModule = new UserModule({ store, name: Modules.USER })
